import { activeUserProfile } from '../../active-userprofile';
import { ApiFactory } from '../../api/agwasuite-api-factory';
import { ApiSessionHealth } from '../../api/core/agwasuite-api-session-health';
import { defaultDomain } from '../../api/default-domain';
import { isSame } from '../strings/strings-utils';
import type { SignInResult } from './connection-health';

/**
 * The two questions the connection-health monitor asks the server (D-137, rule 1). Kept apart from
 * the monitor so the monitor is unit-tested with its own answers and this file is the only one that
 * talks to the network.
 */

/** How long a reachability check waits for `/healthz` before counting the server as unreachable. */
export const REACHABILITY_TIMEOUT_MS = 5_000;

/**
 * Whether the server answers its anonymous liveness check, `/healthz`. Anonymous and outside
 * `/api` on purpose: it must answer whoever is or is not signed in, and it is the one request the
 * client version gate and the sign-in cannot refuse. Never throws: a timeout, a network failure or
 * a non-2xx answer are all "not reachable".
 */
export async function checkServerReachable(): Promise<boolean> {
  try {
    const response = await fetch(`${defaultDomain}/healthz`, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      signal: AbortSignal.timeout(REACHABILITY_TIMEOUT_MS),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Whether the server still signs this tab in as the person it acts as, through
 * `GET /api/SessionHealth` (rule 4, rule 7). Never throws.
 *
 * - No one signed in on this tab: `not-signed-in`, and nothing is asked.
 * - 401 or 403: `refused`, the sign-in has ended.
 * - An answer naming someone else: `mismatch`. The id is compared with isSame, never ===: a GUID
 *   that reaches the client in another casing is still the same person.
 * - Anything else (a 5xx, a timeout, the version gate's 426): `unknown`, which proves nothing.
 */
export async function checkSignIn(): Promise<SignInResult> {
  const profile = activeUserProfile();
  if (!profile?.isLoggedIn) return 'not-signed-in';
  try {
    const response = await ApiFactory.authenticated(ApiSessionHealth).check();
    if (response.success) {
      // A profile with no user id cannot be the person the server names, so it is a mismatch.
      const actingUserId = profile.data.userId;
      return actingUserId && isSame(response.data.loginUserId, actingUserId)
        ? 'ok'
        : 'mismatch';
    }
    return response.status === 401 || response.status === 403
      ? 'refused'
      : 'unknown';
  } catch {
    return 'unknown';
  }
}
